import { endOfMonth, format, isAfter, isBefore, isSameDay, startOfMonth } from 'date-fns';

import { MIN_DATE, MAX_DATE } from '../../utils';

import { Box } from './Box';
import { Header } from './Header';
import { calendarGenerator } from './utils';

interface CalendarCommonProps {
  readonly year: number;
  readonly month: number;
  readonly startDate: Date;
  readonly dates?: [Date | null, Date | null];
  readonly onPickDate?: (date: Date) => void;
}

interface CalendarSingleProps {
  readonly rangePicker?: false;
  readonly endDate?: never;
}

interface CalendarRangeProps {
  readonly rangePicker: true;
  readonly endDate: Date;
}

type CalendarProps = CalendarCommonProps & (CalendarRangeProps | CalendarSingleProps);

function Calendar({ startDate, endDate, year, month, dates, rangePicker, onPickDate }: CalendarProps) {
  const minDate = dates?.[0] ?? MIN_DATE;
  const maxDate = dates?.[1] ?? MAX_DATE;
  const calendar = calendarGenerator({ year, month });
  const firstDate = startOfMonth(new Date(year, month, 1));
  const lastDate = endOfMonth(new Date(year, month, 1));

  return (
    <div role="grid" className="flex flex-auto flex-col">
      <div className="flex flex-row">
        {calendar[0].map((date, i) => (
          <Header key={i} index={i}>
            {format(date, 'EEEEEE')}
          </Header>
        ))}
      </div>
      {calendar.map((dts, i) => (
        <div key={i} className="my-0.5 flex flex-row rounded-md">
          {dts.map((date, n) => {
            const datestamp = +date;
            const isDisabled = datestamp < +minDate || datestamp > +maxDate;
            const isInactive = isBefore(date, firstDate) || isAfter(date, lastDate);
            // true = it is active but not the selected start nor end
            // first = it is active and the selected start date
            // last = it is active and the selected end date
            let _active: true | '' | 'first' | 'last' | 'firstlast' = '';

            if (rangePicker) {
              if (isSameDay(date, startDate)) {
                _active += 'first';
              }

              if (isSameDay(date, endDate)) {
                _active += 'last';
              }

              if (!_active && datestamp < +endDate && datestamp > +startDate) {
                _active = true;
              }
            } else if (isSameDay(date, startDate)) {
              _active = 'firstlast';
            }

            return (
              <Box
                key={n}
                index={n}
                date={date}
                disabled={isDisabled}
                inactive={isInactive}
                active={_active as 'firstlast'}
                onClick={onPickDate}
              >
                {date.getDate()}
              </Box>
            );
          })}
        </div>
      ))}
    </div>
  );
}

export { Calendar, MIN_DATE, MAX_DATE };
